import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { getAllCaseStudies } from "@/content/case-studies";
import IllustrativeDisclaimer from "@/components/site/IllustrativeDisclaimer";
import Seo from "@/lib/Seo";

// Filter buckets — each maps to `meta.tags` on the study data.
const FILTER_TAGS = [
  { id: "all", label: "All", match: null },
  { id: "sba", label: "SBA", match: ["sba"] },
  { id: "working-capital", label: "Working Capital", match: ["working-capital"] },
  { id: "real-estate", label: "Real Estate", match: ["real-estate"] },
  { id: "equipment", label: "Equipment", match: ["equipment"] },
  { id: "bad-credit", label: "Bad Credit", match: ["bad-credit"] },
];

export default function CaseStudies() {
  const studies = getAllCaseStudies();
  const [active, setActive] = useState("all");

  const counts = useMemo(() => {
    const c = { all: studies.length };
    FILTER_TAGS.filter((f) => f.match).forEach((f) => {
      c[f.id] = studies.filter((s) =>
        (s.tags || []).some((t) => f.match.includes(t))
      ).length;
    });
    return c;
  }, [studies]);

  const filtered = useMemo(() => {
    const f = FILTER_TAGS.find((x) => x.id === active);
    if (!f || !f.match) return studies;
    return studies.filter((s) =>
      (s.tags || []).some((t) => f.match.includes(t))
    );
  }, [active, studies]);

  return (
    <div>
      <Seo
        path="/deal-structures"
        title="Deal Structures — How Brokers Actually Structure a Business Loan"
        description="Illustrative walkthroughs of how a working commercial finance broker structures common business-funding files. SBA, working capital, real estate, equipment, and bad-credit scenarios. Educational — not specific closings."
      />

      {/* Editorial hero */}
      <section className="pt-40 pb-16 border-b border-black/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="overline">Deal structures · Educational walkthroughs</div>
          <h1 className="mt-8 font-serif text-6xl md:text-8xl leading-[0.9] tracking-tight max-w-5xl">
            How the <em className="italic text-[color:var(--gold)]">files</em> actually get built.
          </h1>
          <div className="mt-10 h-px w-12 bg-[color:var(--gold)]" />
          <p className="mt-10 max-w-2xl text-[color:var(--bone)]/75 leading-relaxed text-lg">
            Illustrative walkthroughs of how a working commercial finance
            broker structures common file types — the way you'd brief a junior
            broker on Monday morning. Situation, structure, outcome. No
            borrower names because there are no specific borrowers.
          </p>

          <div className="mt-10 max-w-2xl">
            <IllustrativeDisclaimer />
          </div>

          {/* Filter pills */}
          <div className="mt-10 flex flex-wrap gap-2" data-testid="deal-structure-filter">
            {FILTER_TAGS.map((f) => {
              const isActive = active === f.id;
              return (
                <button
                  key={f.id}
                  data-testid={`deal-structure-filter-${f.id}`}
                  onClick={() => setActive(f.id)}
                  className={`px-4 py-2 font-mono text-[11px] tracking-[0.22em] uppercase rounded-full border transition-all duration-300 ${
                    isActive
                      ? "bg-[color:var(--gold)] text-black border-[color:var(--gold)]"
                      : "border-[color:var(--bone)]/25 text-[color:var(--bone)]/70 hover:border-[color:var(--gold)] hover:text-[color:var(--gold)]"
                  }`}
                >
                  {f.label} · {counts[f.id] ?? 0}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Studies grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          {filtered.length === 0 ? (
            <div className="py-24 text-center">
              <div className="overline">No deal structures in this filter yet</div>
              <button
                onClick={() => setActive("all")}
                className="mt-6 gold-link font-mono text-xs tracking-[0.24em] uppercase text-[color:var(--gold)]"
              >
                View all →
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-12 gap-8">
              {filtered.map((s, i) => (
                <motion.article
                  key={s.slug}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.7, delay: (i % 6) * 0.06 }}
                  className="col-span-12 md:col-span-6 border-t border-black/10 pt-8"
                >
                  <Link
                    to={`/deal-structures/${s.slug}`}
                    data-testid={`deal-structure-card-${s.slug}`}
                    className="group block"
                  >
                    <div className="relative aspect-[16/10] overflow-hidden mb-6">
                      <img
                        src={s.image}
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-black/18" />
                      <div className="absolute top-5 left-5 font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--gold)]">
                        {s.industry}
                      </div>
                      <div className="absolute bottom-5 left-5 right-5 flex items-baseline justify-between gap-3 flex-wrap">
                        <span className="font-serif text-3xl md:text-4xl text-[color:var(--gold)]">
                          {s.amount}
                        </span>
                        <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[color:var(--bone)]">
                          Funded in {s.timeToFund}
                        </span>
                      </div>
                    </div>
                    <div className="inline-flex items-center px-2.5 py-1 border border-[color:var(--bone)]/20 rounded-full font-mono text-[9px] tracking-[0.22em] uppercase text-[color:var(--bone)]/60">
                      {s.readTime}
                    </div>
                    <h2 className="mt-4 font-serif text-3xl md:text-4xl leading-tight group-hover:text-[color:var(--gold)] transition-colors">
                      {s.title}
                    </h2>
                    <p className="mt-4 text-[color:var(--bone)]/75 leading-relaxed">
                      {s.excerpt}
                    </p>
                    <span className="mt-6 inline-flex items-center gap-2 font-mono text-xs tracking-[0.24em] uppercase text-[color:var(--gold)]">
                      Read the walkthrough <ArrowRight size={14} />
                    </span>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
